import {
	Invoice
} from '@/types'

const NET_TERMS_PATTERN = /^\s*net\s*(\d+)\s*$/i
const ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})/
const US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/

export const PAST_DUE_STATUS = 'Past Due'

// the statuses an invoice can still be owed money on, so a second pass over an invoice that has
// already been marked past due does not silently stop counting it
const OPEN_INVOICE_STATUSES = ['Invoiced', PAST_DUE_STATUS]

// determines whether an invoice's payment terms count as "due on receipt"
export function isDueOnReceipt(paymentTerms: string) {
	return paymentTerms?.toLowerCase().includes('due on receipt')
}

// returns a copy of the given date with the time cleared, so comparisons between dates
// ignore time of day. never mutates the date passed in
export function startOfDay(date: Date) {
	const dayStart = new Date(date)

	dayStart.setHours(0, 0, 0, 0)

	return dayStart
}

// returns a copy of the given date moved forward by the given number of days. never mutates the
// date passed in
export function addDays(date: Date, days: number) {
	const shiftedDate = new Date(date)

	shiftedDate.setDate(shiftedDate.getDate() + days)

	return shiftedDate
}

// parses a date string off an invoice into a local midnight Date. handled explicitly rather than
// through the Date constructor because ISO style strings are parsed as UTC, which shifts them
// back a day in western timezones
export function parseDateString(dateString: string) {
	if (!dateString) {
		return null
	}

	const isoDate = dateString.match(ISO_DATE_PATTERN)
	if (isoDate) {
		return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]))
	}

	const usDate = dateString.match(US_DATE_PATTERN)
	if (usDate) {
		return new Date(Number(usDate[3]), Number(usDate[1]) - 1, Number(usDate[2]))
	}

	const parsedDate = new Date(dateString)
	if (isNaN(parsedDate.getTime())) {
		return null
	}

	return startOfDay(parsedDate)
}

// returns the due date for an invoice: net terms push the due date out from the invoice date,
// anything else (due on receipt) is due the day it was invoiced
export function resolveDueDate(invoice: Invoice) {
	const netTerms = invoice.paymentTerms?.match(NET_TERMS_PATTERN)
	if (!netTerms) {
		return invoice.invoiceDate
	}

	const invoiceDate = parseDateString(invoice.invoiceDate)
	if (!invoiceDate) {
		return invoice.invoiceDate
	}

	return addDays(invoiceDate, Number(netTerms[1])).toLocaleDateString('en-US')
}

// returns what the customer actually owes on an invoice, with any credit card processing fee
// removed since that is only charged when they choose to pay by card
export function amountDueWithoutProcessingFee(invoice: Invoice) {
	if (invoice.amountDue <= 0 || invoice.status === 'Paid') {
		return 0
	}

	const processingFee = invoice.services?.find((service: {description: string, total: number}) => {
		return service.description?.toLowerCase().includes('processing fee')
	})

	const amountDue = invoice.amountDue - (processingFee?.total ?? 0)

	if (amountDue < 0) {
		return 0
	}

	return amountDue
}

// an invoice is past due on or after its due date, so a Net 30 invoice is past due 30 days after
// it was invoiced and a due on receipt invoice the day it was invoiced. the comparison is date to
// date so it flips at midnight rather than partway through the day. the due date is resolved here
// rather than read off invoice.dueDate so the answer does not depend on applyInvoiceDueStatus
// having run first
export function isPastDue(invoice: Invoice, asOf: Date = new Date()) {
	if (!OPEN_INVOICE_STATUSES.includes(invoice.status)) {
		return false
	}

	if (amountDueWithoutProcessingFee(invoice) <= 0) {
		return false
	}

	const dueDate = parseDateString(resolveDueDate(invoice))
	if (!dueDate) {
		return false
	}

	return startOfDay(asOf).getTime() >= dueDate.getTime()
}

// fills in an invoice's derived due date and past due status. mutating the passed invoice is the
// point: it is the row object the invoice tables render from
export function applyInvoiceDueStatus(invoice: Invoice, asOf: Date = new Date()) {
	invoice.dueDate = resolveDueDate(invoice)
	invoice.isOverdue = isPastDue(invoice, asOf)

	if (!invoice.isOverdue) return

	invoice.status = PAST_DUE_STATUS
}

// an invoice can be paid while it is still open and no payment is already in flight against it
export function isPayable(invoice: Invoice) {
	return OPEN_INVOICE_STATUSES.includes(invoice.status) && invoice.paymentStatus !== 'pending'
}

// totals what is owed across a set of invoices, excluding processing fees
export function sumAmountDue(invoices: Invoice[]) {
	return invoices.reduce((total: number, invoice: Invoice) => {
		return total + amountDueWithoutProcessingFee(invoice)
	}, 0)
}

// determines whether to show the status badge instead of the raw payment terms
export function showStatusBadge(invoice: Invoice) {
	return !isDueOnReceipt(invoice.paymentTerms)
		|| invoice.status === 'Paid'
		|| invoice.paymentStatus === 'pending'
		|| invoice.paymentStatus === 'pending_verification'
		|| invoice.paymentStatus === 'failed'
}

// clears a pending verification status on the local invoice so the status badge and pay button
// update immediately after a cancellation, ahead of the next fetch from FileMaker. mutating the
// passed invoice is the point: it is the row object the table renders from
export function clearPendingVerification(invoice: Invoice) {
	if (invoice.paymentStatus !== 'pending_verification') return

	invoice.paymentStatus = ''
}

// maps an invoice's status/paymentStatus combo to the badge color used to represent it
export function invoiceStatusColor(invoice: Invoice) {
	switch(invoice.paymentStatus) {
		case 'pending':
			return 'yellow'
		case 'pending_verification':
			return 'orange'
		case 'failed':
			return 'red'
	}

	switch(invoice.status) {
		case 'Paid':
			return 'green'
		case PAST_DUE_STATUS:
			return 'red'
		case 'Cancelled':
			return 'gray'
		default:
			return 'yellow'
	}
}

// returns the display label for an invoice's status/paymentStatus combo
export function invoiceStatusLabel(invoice: Invoice) {
	switch(invoice.paymentStatus) {
		case 'pending':
			return 'Payment Pending'
		case 'pending_verification':
			return 'Verification Pending'
		case 'failed':
			return 'Payment Failed'
		default:
			return invoice.status
	}
}
